import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { BookOpen, Download, Bookmark, Share2, Loader2 } from 'lucide-react';
import { Progress } from './ui/progress';
import { ShareDialog } from './ShareDialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGeneration } from '../contexts/GenerationContext';
import { useBookmark } from '../hooks/useBookmark';
import { useGenerateBook } from '../hooks/useGenerateBook';

export function BookCard({ book, onSelectBook, isSaved: isSavedProp, skipLibraryCheck = false, onShowDetails, onSavedChanged, showDownload = false }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { getGeneration } = useGeneration();

  const { isSaved, isSaving, toggle: handleBookmark, setIsSaved } = useBookmark(book, {
    initialSaved: isSavedProp,
    skipLibraryCheck,
    onChanged: onSavedChanged,
  });

  const { start: startGenerate } = useGenerateBook();

  // Get persistent generation status from context
  const generation = getGeneration(book.id);
  const isGenerating = book.status === 'processing' || generation?.status === 'processing';
  const isComplete = book.status === 'complete' || generation?.status === 'complete';
  const isError = book.status === 'error' || generation?.status === 'error';

  const handleAddToLibrary = async () => {
    const result = await startGenerate(book);
    if (result.ok) setIsSaved(true);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    // TODO: implement offline download
    toast({ title: "Coming soon", description: "Offline downloads will be available in a future update." });
  };

  // Share handled by ShareDialog component (link + QR code).
  const handleShareClick = (e) => {
    e.stopPropagation();
  };

  const handleCardClick = () => {
    if (onShowDetails) {
      onShowDetails(book);
    }
  };

  /* Outer card is keyboard-activatable as a "view details" button (WCAG 2.1.1
   * + 4.1.2). Inner buttons (bookmark, share, etc.) stop propagation so the
   * outer activation only fires on the card surface itself. Nested-interactive
   * compromise pending a future design change. */
  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <Card
      className="h-full flex flex-col hover:shadow-lg hover:bg-accent/30 transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${book.title} by ${book.author}`}
    >
      <CardHeader className="relative">
        {user && (
          <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleBookmark}
              disabled={isSaving}
              aria-label={isSaved ? "Remove from library" : "Save to library"}
            >
              <Bookmark className={`h-4 w-4 ${isSaved ? 'fill-current text-primary' : ''}`} />
            </Button>
            {showDownload && isComplete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleDownload}
                aria-label="Download for offline"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
            <ShareDialog book={book}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleShareClick}
                aria-label="Share"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </ShareDialog>
          </div>
        )}
        <div className="pr-12">
          <CardTitle className="text-lg line-clamp-2 font-serif">{book.title}</CardTitle>
          <CardDescription className="mt-1">by {book.author}</CardDescription>
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        <div className="flex items-center gap-2 text-sm text-foreground/70">
          <BookOpen className="h-4 w-4" />
          <span>Classic Literature</span>
        </div>
      </CardContent>

      <CardFooter className="flex flex-col gap-2">
        {isComplete ? (
          <Button
            onClick={(e) => { e.stopPropagation(); onSelectBook(book.id); }}
            className="w-full"
            size="lg"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            Read Now
          </Button>
        ) : isGenerating ? (
          <div className="w-full py-3 space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Preparing</span>
            </div>
            {generation?.chunksReady > 0 && generation?.totalChunkCount > 0 && (
              <Progress
                value={(generation.chunksReady / generation.totalChunkCount) * 100}
                className="h-2"
              />
            )}
          </div>
        ) : isError ? (
          <Button
            onClick={(e) => { e.stopPropagation(); handleAddToLibrary(); }}
            className="w-full"
            variant="outline"
          >
            Try Again
          </Button>
        ) : (
          <Button
            onClick={(e) => { e.stopPropagation(); handleAddToLibrary(); }}
            className="w-full"
            variant="outline"
          >
            Add to Library
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
